<template>
    <AppHead title="Award Bookings" />

    <div class="px-4 md:px-8 py-2 md:py-4 relative">
        <!-- Header -->
        <div
            class="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-6"
        >
            <div>
                <h2 class="text-2xl font-semibold text-slate-800 dark:text-slate-100">
                    Your award booking trips
                </h2>
                <p class="text-slate-500 dark:text-slate-400 text-sm mt-1">
                    Effortlessly book flights with your points and miles, let
                    our experts handle everything for you.
                </p>
                <a
                    href="#"
                    class="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 underline mt-1 inline-block"
                >
                    About award booking
                </a>
            </div>
            <button
                @click="requestNewBooking"
                class="mt-4 md:mt-0 inline-flex items-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
                <Icon icon="plus" :width="16" :height="16" />
                Request new booking
            </button>
        </div>

        <!-- Tabs -->
        <div class="border-b border-slate-200 dark:border-slate-700 mb-6">
            <nav class="flex gap-6" aria-label="Tabs">
                <button
                    @click="setActiveTab('active')"
                    :class="[
                        'pb-3 text-sm font-medium border-b-2 transition-colors',
                        activeTab === 'active'
                            ? 'border-orange-500 text-slate-800 dark:text-slate-100'
                            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200',
                    ]"
                >
                    Active
                </button>
                <button
                    @click="setActiveTab('completed')"
                    :class="[
                        'pb-3 text-sm font-medium border-b-2 transition-colors',
                        activeTab === 'completed'
                            ? 'border-orange-500 text-slate-800 dark:text-slate-100'
                            : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200',
                    ]"
                >
                    Completed
                </button>
            </nav>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
            <div
                class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"
            ></div>
        </div>

        <!-- Bookings List -->
        <div v-else-if="currentBookings.length > 0" class="space-y-4">
            <BookingCard
                v-for="booking in currentBookings"
                :key="booking.id"
                :booking="booking"
            />
        </div>

        <!-- Empty State -->
        <div
            v-else
            class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-8 text-center"
        >
            <div class="text-slate-400 mb-4">
                <Icon icon="plane" :width="48" :height="48" class="mx-auto" />
            </div>
            <h3 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-2">
                No {{ activeTab }} bookings
            </h3>
            <p class="text-slate-500 dark:text-slate-400 text-sm mb-4">
                {{
                    activeTab === 'active'
                        ? "You don't have any active booking requests. Start by requesting a new booking."
                        : "You don't have any completed bookings yet."
                }}
            </p>
            <button
                v-if="activeTab === 'active'"
                @click="requestNewBooking"
                class="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
                <Icon icon="plus" :width="16" :height="16" />
                Request new booking
            </button>
        </div>
    </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { router } from '@inertiajs/vue3';
import { useAwardBookingsStore } from '@/store/award-bookings';
import AppHead from '@/Partials/AppHead.vue';
import Icon from '@/components/ui/icon/icon.vue';
import BookingCard from '@/components/award-bookings/booking-card.vue';

const awardBookingsStore = useAwardBookingsStore();
const { activeTab, loading, currentBookings } = storeToRefs(awardBookingsStore);

const setActiveTab = (tab) => {
    awardBookingsStore.setActiveTab(tab);
};

const requestNewBooking = () => {
    router.visit('/award-bookings/new');
};

onMounted(() => {
    awardBookingsStore.fetchAllBookings();
});
</script>
